import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Compass, ExternalLink, Inbox, RefreshCw, Rss } from 'lucide-react';
import { HUB_PROVIDERS } from '@repo/shared-types';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SlackCard } from '../components/integrations/SlackCard';
import { IngestQueue } from '../components/integrations/IngestQueue';
import { ProviderSection } from '../components/sources/ProviderSection';
import { api, type Integration, type SourcesOverview } from '../lib/api';
import { useSources } from '../lib/sources';
import { useProjectTree } from '../lib/project-tree';
import { cn, timeAgo } from '../lib/utils';

const TABS = [
  { key: 'sources', label: 'Sources', icon: Rss },
  { key: 'waiting', label: 'Waiting', icon: Inbox },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/** What memory receives, and where it goes. The sources hub collects; this page subscribes. */
export default function Sources() {
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab: TabKey = TABS.some((t) => t.key === requested) ? (requested as TabKey) : 'sources';

  return (
    <Layout title="Sources" subtitle="What memory receives, and where it goes">
      <nav
        role="tablist"
        aria-label="Sources"
        className="sticky top-0 z-10 -mx-8 px-8 -mt-2 pt-2 bg-surface flex items-center gap-1 border-b border-border mb-6"
      >
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={active}
              onClick={() => setParams(key === 'sources' ? {} : { tab: key }, { replace: true })}
              className={cn(
                'flex items-center gap-2 px-3 py-2 -mb-px text-sm font-medium border-b-2 transition-colors',
                active
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                  : 'border-transparent text-ink-muted hover:text-ink',
              )}
            >
              <Icon size={14} />
              {label}
            </button>
          );
        })}
      </nav>

      {tab === 'sources' ? <SourcesTab /> : <IngestQueue />}
    </Layout>
  );
}

function SourcesTab() {
  const { overview, error, busyKey, save, sync, pull } = useSources();
  const [slack, setSlack] = useState<Integration | null>(null);

  const loadSlack = useCallback(() => {
    api.integrations
      .list()
      .then((rows) => setSlack(rows.find((r) => r.provider === 'SLACK') ?? null))
      .catch(() => setSlack(null));
  }, []);
  useEffect(loadSlack, [loadSlack]);

  return (
    <div className="space-y-6">
      <HowItWorks consoleUrl={overview?.hub.consoleUrl ?? null} />

      {error && <p className="text-sm text-danger-text bg-danger-bg px-3 py-2 rounded-lg">{error}</p>}

      {overview && (
        <>
          <HubStatus overview={overview} busyKey={busyKey} onPull={pull} onSync={sync} />
          {overview.hub.reachable &&
            HUB_PROVIDERS.map((provider) => (
              <ProviderSection
                key={provider}
                provider={provider}
                sources={overview.sources.filter((s) => s.provider === provider)}
                consoleUrl={overview.hub.consoleUrl}
                busyKey={busyKey}
                onSave={save}
              />
            ))}
        </>
      )}

      <SlackCard scope="GLOBAL" integration={slack} onChanged={loadSlack} />

      <RoutingDestinations />
    </div>
  );
}

function HowItWorks({ consoleUrl }: { consoleUrl: string | null }) {
  const steps = [
    {
      title: 'The sources hub collects',
      body: (
        <>
          Granola accounts, mailboxes, Shared Drives and calendars are connected in the{' '}
          {consoleUrl ? (
            <a href={consoleUrl} target="_blank" rel="noreferrer" className="text-orange-500 hover:text-orange-600 underline">
              hub console
            </a>
          ) : (
            'hub console'
          )}
          . Everything it collects is listed below.
        </>
      ),
    },
    {
      title: 'You choose what memory receives',
      body: 'Give a source a destination to receive it: the organization, where the ingestor places each item, or specific projects. Remove every destination to stop.',
    },
    {
      title: 'The ingestor files it',
      body: 'Arrivals wait for the nightly window, 00:00 Pacific, and are then written into pages. The Waiting tab shows what is queued.',
    },
  ];
  return (
    <ol className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {steps.map((step, i) => (
        <li key={step.title} className="flex gap-3 rounded-lg border border-border bg-surface-subtle px-4 py-3">
          <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-orange-500/10 text-[11px] font-semibold text-orange-600 dark:text-orange-400">
            {i + 1}
          </span>
          <div className="text-xs text-ink-muted leading-relaxed">
            <p className="text-sm font-medium text-ink-secondary">{step.title}</p>
            {step.body}
          </div>
        </li>
      ))}
    </ol>
  );
}

function HubStatus({
  overview,
  busyKey,
  onPull,
  onSync,
}: {
  overview: SourcesOverview;
  busyKey: string | null;
  onPull: () => void;
  onSync: () => void;
}) {
  const { hub, pull, sync } = overview;

  if (!hub.configured) {
    return (
      <Notice>
        This deployment isn't connected to a sources hub. Set <code>SOURCES_HUB_URL</code> and{' '}
        <code>SOURCES_HUB_API_KEY</code>, which a hub operator issues when registering this app.
      </Notice>
    );
  }
  if (!hub.reachable) return <Notice>Can't reach the sources hub: {hub.error}</Notice>;

  const pending = sync.missing.length + sync.extra.length;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-border px-4 py-2.5 text-xs text-ink-muted">
        <span className="flex items-center gap-1.5 text-ink-secondary">
          <span className="h-2 w-2 rounded-full bg-success-text" />
          Receiving as <span className="font-mono">{hub.appName}</span>
        </span>
        <span>{hub.pushEnabled ? 'Push: on' : 'Push: off — arrivals come by the 15-minute pull'}</span>
        <span>{pull.lastPulledAt ? `Last pull ${timeAgo(pull.lastPulledAt)}` : 'Never pulled'}</span>
        {pull.lastError && <span className="text-warning-text">Last pull failed: {pull.lastError}</span>}
        <span className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="ghost" disabled={busyKey === 'pull'} onClick={onPull}>
            <RefreshCw size={12} />
            Pull now
          </Button>
          {hub.consoleUrl && (
            <a
              href={hub.consoleUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-orange-500 hover:text-orange-600"
            >
              Hub console <ExternalLink size={11} />
            </a>
          )}
        </span>
      </div>

      {!sync.inSync && (
        <Notice
          action={
            <Button size="sm" variant="primary" disabled={busyKey === 'sync'} onClick={onSync}>
              Send to hub
            </Button>
          }
        >
          The hub's subscription list doesn't match what's saved here ({pending}{' '}
          {pending === 1 ? 'difference' : 'differences'}). Until it's sent, the hub delivers the old list.
        </Notice>
      )}
    </div>
  );
}

function Notice({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-warning-bg px-4 py-3 text-sm text-warning-text">
      <AlertTriangle size={15} className="flex-shrink-0" />
      <p className="flex-1">{children}</p>
      {action}
    </div>
  );
}

/** The projects the ingestor chooses between, and what it knows about each. */
function RoutingDestinations() {
  const { allProjects, loading } = useProjectTree();

  if (loading) return null;

  const described = allProjects.filter((p) => p.description?.trim()).length;

  return (
    <Card>
      <details>
        <summary className="flex cursor-pointer items-center gap-2 px-6 py-4 list-none">
          <Compass size={15} className="text-ink-faint" />
          <CardTitle>Where the organization's items can go</CardTitle>
          <span className="ml-auto text-[11px] text-ink-faint">
            {described}/{allProjects.length} projects describe themselves · show
          </span>
        </summary>
      <CardContent className="border-t border-border-subtle">
        <ul className="divide-y divide-border-subtle">
          {allProjects.map((project) => (
            <li key={project.id} className="py-2 flex items-start gap-3" style={{ paddingLeft: project.depth * 14 }}>
              <div className="min-w-0 flex-1">
                <Link
                  to={`/p/${project.slug}/settings`}
                  className="text-sm font-medium text-ink-secondary hover:text-orange-500 transition-colors"
                >
                  {project.name}
                </Link>
                {project.description?.trim() ? (
                  <p className="text-xs text-ink-muted leading-relaxed mt-0.5">{project.description}</p>
                ) : (
                  // Descriptions are written from a project's pages, so the fix is no longer "write
                  // one" — it is to put something in the project. Saying "add one" would point at a
                  // text box that no longer exists.
                  <p className="text-xs text-ink-faint mt-0.5">
                    Not described yet — the ingestor has only this project's existing pages to judge by.
                    A description is written once it has a few.{' '}
                    <Link
                      to={`/p/${project.slug}/kb`}
                      className="text-orange-500 hover:text-orange-600 underline"
                    >
                      Add something to it
                    </Link>
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
      </details>
    </Card>
  );
}
